import React from 'react';
import './LongTermScheduleCalendar.scss';
import {
    Button,
    FormGroup,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCancel,
    faClock,
    faCommentSlash,
    faSave,
} from '@fortawesome/free-solid-svg-icons';
import { FormLabel, toasty } from '../common/forms/FormElements';
import { util } from '../Util';
import { ApiRoutes } from '../ApiRoutes';
import moment from 'moment';
import ConstrainedTimeInput from '../common/forms/ConstrainedTimeInput';

export default class JobStartTimeButton extends React.Component {
    constructor(props) {
        super(props);

        const { job } = props;

        this.state = {
            updatedJobStartTime: job?.notes ?? '',
            showModal: false,
            anyChanges: false,
            saving: false,
        };
    }

    toggleModal = () => {
        const { showModal } = this.state;

        if (!showModal) {
            const { job } = this.props;

            this.setState({
                updatedJobStartTime: job.startTime,
                showModal: true,
                anyChanges: false,
            });
        } else {
            this.setState({ showModal: false });
        }
    };

    onJobStartTimeChange = (e) => {
        const updatedJobStartTime = e.target.value ?? '';
        this.setState({ updatedJobStartTime, anyChanges: true });
    };

    onSave = async () => {
        const { saving } = this.state;

        if (saving) {
            return;
        }

        this.setState({ saving: true });

        const { job, onJobStartTimeUpdated } = this.props;
        const { updatedJobStartTime } = this.state;

        try {
            const jobResponse = await util.fetch.andGetResponse(
                util.fetch.types.post,
                ApiRoutes.job.updateStartTime(job.id),
                updatedJobStartTime
            );

            if (jobResponse) {
                onJobStartTimeUpdated(updatedJobStartTime);

                toasty.success('Start Time Updated');

                this.toggleModal();
            }
        } catch (e) {
            console.error({ e });
        }

        this.setState({ saving: false });
    };

    renderNotesOrNotSpecified = () => {
        const { job } = this.props;

        if (job.notes) {
            return <pre>{job.notes}</pre>;
        }

        return (
            <div className="d-flex">
                <div>
                    <FontAwesomeIcon icon={faCommentSlash} className="mr-1" />
                </div>
                <div>None specified</div>
            </div>
        );
    };

    getIsLtsInThePast = () => {
        const { longTermSchedule } = this.props;

        return moment(longTermSchedule.endDate).isBefore(new Date(), 'day');
    };

    isValidTime = (timeHourInput) => {
        const parsedStartTime = moment(
            timeHourInput,
            ['HH:mm', 'HH:mm:ss'],
            true
        );

        return parsedStartTime.isValid();
    };

    getParsedTime = (timeHourInput) =>
        moment(timeHourInput, ['HH:mm', 'HH:mm:ss'], true);

    renderStartTime = () => {
        const { job } = this.props;
        const startTime = job.startTime;

        if (!this.isValidTime(startTime)) {
            console.error('Unknown job start time encountered.');
            console.error({ job });
            return startTime;
        }

        const parsedStartTime = this.getParsedTime(job.startTime);

        return <>{parsedStartTime.format('h:mm A')}</>;
    };

    getIsUpdatedStartTimeInvalid = () => {
        const { updatedJobStartTime } = this.state;
        const parsedStartTime = moment(
            updatedJobStartTime,
            ['HH:mm', 'HH:mm:ss'],
            true
        );

        return !parsedStartTime.isValid();
    };

    render() {
        const { job } = this.props;

        if (!job) {
            return null;
        }

        const { showModal, updatedJobStartTime, anyChanges, saving } =
            this.state;

        const isLtsInThePast = this.getIsLtsInThePast();
        const isUpdatedStartTimeInvalid = this.getIsUpdatedStartTimeInvalid();

        return (
            <>
                <Button
                    type="button"
                    color="info"
                    outline
                    className="mb-2 w-100"
                    onClick={this.toggleModal}
                    disabled={isLtsInThePast}
                >
                    <div className="d-flex fg-2">
                        <div className="mr-2 text-left w-33 btn-title">
                            <FontAwesomeIcon icon={faClock} className="mr-2" />
                            Start Time
                        </div>
                        <div className="wrap-container w-100 text-left">
                            {this.renderStartTime()}
                        </div>
                    </div>
                </Button>

                <Modal size="xl" isOpen={showModal} toggle={this.toggleModal}>
                    <ModalHeader toggle={this.toggleModal}>
                        Updating Start Time
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup className="updated-job-start-time">
                            <FormLabel text="Updated Order Start Time" />

                            <ConstrainedTimeInput
                                value={updatedJobStartTime}
                                onChange={this.onJobStartTimeChange}
                                invalid={isUpdatedStartTimeInvalid}
                            />
                        </FormGroup>

                        <hr />
                        <div>
                            As a reminder, the original job start time was:
                        </div>

                        {this.renderStartTime()}
                    </ModalBody>
                    <ModalFooter>
                        <Row className="w-100">
                            <div className="col-6">
                                <Button
                                    type="button"
                                    color="warning"
                                    onClick={this.toggleModal}
                                    className="float-left"
                                >
                                    <FontAwesomeIcon
                                        icon={faCancel}
                                        className="mr-2"
                                    />
                                    Cancel
                                </Button>
                            </div>
                            <div className="col-6">
                                <Button
                                    type="button"
                                    color="success"
                                    className="float-right"
                                    disabled={
                                        !anyChanges ||
                                        saving ||
                                        isUpdatedStartTimeInvalid
                                    }
                                    outline={!anyChanges}
                                    onClick={this.onSave}
                                >
                                    <FontAwesomeIcon
                                        icon={faSave}
                                        className="mr-2"
                                        pulse={saving}
                                    />
                                    Update Start Time
                                </Button>
                            </div>
                        </Row>
                    </ModalFooter>
                </Modal>
            </>
        );
    }
}
